import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {FormActionField, FORM_FIELD_SETTINGS_MODAL_TYPE} from 'forms/form-designer/Constants';
import FormGroup from 'ui-library/bs/FormGroup';
import TextArea from 'ui-library/bs/TextArea';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

export interface FormFieldSettingsModalProps {
  field: FormActionField;
  onRequestSubmit: (updatedField: FormActionField) => void;
  onRequestClose: () => void;

  // Data for analytics payload
  formDefinitionId: string | null;
  actionOrigin: ActionOrigin;
  sessionId: string;
}

/**
 * A modal that allows the user to edit a Form field, used in the form designer.
 * Note that the updated field.description may be null in the onRequestSubmit callback.
 *
 * @param field The original form field to edit, which is treated as immutable.
 * @param onRequestSubmit Callback called on a successful submit with the updated form field.
 * @param onRequestClose Callback on closing modal without saving
 * @returns React Modal element
 */
export function FormFieldSettingsModal({
  field,
  onRequestSubmit,
  onRequestClose,
  formDefinitionId,
  actionOrigin,
  sessionId,
}: FormFieldSettingsModalProps): JSX.Element {
  const schemaFieldName = field.actionParameter.name;
  const originalFieldName = field.displayName;

  const fieldNameInput = useFormInput(originalFieldName);
  const [fieldDescription, setFieldDescription] = useState(field.description || '');
  const [isRequired, setIsRequired] = useState<boolean>(field.required);

  function trackAnalyticsEvent(event: Analytics.AnalyticsEvent): void {
    const newDescription = fieldDescription?.trim() || null;
    const payload: Record<string, string | number | boolean | null> = {
      session_id: sessionId,
      action_origin: actionOrigin,
      modal_type: FORM_FIELD_SETTINGS_MODAL_TYPE,

      form_definition_id: formDefinitionId,
      form_field_key: field.key,
      schema_field_api_id: field.actionParameter.schemaFieldId,
      action_parameter_id: field.actionParameter.id,
      schema_field_name: schemaFieldName,
      field_required: isRequired,
      field_included: field.included,
      field_name_length: fieldNameInput.value.length,
      field_description_length: newDescription?.length ?? 0,
    };

    // Log updated name and description if they have changed (case-insensitive)
    if (fieldNameInput.value.trim().toLowerCase() !== originalFieldName.trim().toLowerCase()) {
      payload.field_name_updated = fieldNameInput.value;
    }
    if (newDescription?.toLowerCase() !== field.description?.trim().toLowerCase()) {
      payload.field_description_updated = newDescription ?? null;
    }

    Analytics.trackEventWithModule(event, ANALYTICS_MODULE.FORMS, payload);
  }

  return (
    <Modal
      onRequestClose={() => {
        trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL);
        onRequestClose();
      }}
      onRequestSubmit={() => {
        trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL);
        onRequestSubmit({
          ...field,
          displayName: fieldNameInput.value,
          description: fieldDescription?.trim() || null,
          required: isRequired,
        });
      }}
      title={`${originalFieldName} settings`}
      buttons={[
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: !fieldNameInput.value,
        },
      ]}
    >
      <div>
        <FormGroup>
          <FieldLabel label="Schema field name" />
          <br />
          {schemaFieldName}
        </FormGroup>

        <FormGroup>
          <FieldLabel label="Display name" htmlFor="form_field_display_name" isRequired={true} />
          <input id="form_field_display_name" type="text" {...fieldNameInput} />
        </FormGroup>

        <FormGroup>
          <FieldLabel label="Description" htmlFor="form_field_description" />
          <TextArea
            id="form_field_description"
            value={fieldDescription}
            onChange={(e) => {
              setFieldDescription(e.target.value);
            }}
          />
        </FormGroup>

        <FormGroup>
          <CheckboxInput
            className="formDesigner-formFieldRequired"
            id="form_field_required"
            label="Make this field required in the form"
            checked={isRequired}
            disabled={field.actionParameter.isRequired}
            onChange={(e) => {
              setIsRequired(e.target.checked);
            }}
          />
        </FormGroup>
      </div>
    </Modal>
  );
}
